// src/hooks/useBoundStore.js
import { useEffect, useState } from "react";

/** Keeps localStorage storeName in sync with the device binding. */
export function syncBoundStoreName() {
  try {
    const orgId   = localStorage.getItem("orgId")   || "";
    const storeId = localStorage.getItem("storeId") || "";
    // Prefer an explicit name saved during linking:
    let name = localStorage.getItem("storeName") || "";

    // If a previous staging session left a value behind, scrub it.
    if (name && /staging/i.test(name)) {
      name = "";
      localStorage.removeItem("storeName");
    }

    // If we have no saved name but *are* bound, fall back to a friendly label.
    if (!name && orgId && storeId) name = localStorage.getItem("storeLabel") || "";

    // Normalize & save once so all tabs/pages read the same key.
    if (name) localStorage.setItem("storeName", name);

    // also expose for the page to re-render on change
    window.dispatchEvent(new Event("storeName:sync"));
  } catch {}
}

/** Read the bound store name reactively (updates when localStorage changes). */
export function useBoundStoreName() {
  const [name, setName] = useState(() =>
    localStorage.getItem("storeName") || ""
  );

  useEffect(() => {
    const onChange = () =>
      setName(localStorage.getItem("storeName") || "");
    window.addEventListener("storage", onChange);
    window.addEventListener("storeName:sync", onChange);
    return () => {
      window.removeEventListener("storage", onChange);
      window.removeEventListener("storeName:sync", onChange);
    };
  }, []);

  return name;
}


/**
 * Reactive device binding identity.
 * Critical for relinks: mounted kiosk pages must not keep a previous storeId
 * captured in a useMemo while the visible storeName has already changed.
 */
export function useBoundStoreIdentity() {
  const read = () => ({
    orgId: localStorage.getItem("orgId") || "",
    storeId: localStorage.getItem("storeId") || "",
    storeName:
      localStorage.getItem("storeName") ||
      localStorage.getItem("storeLabel") ||
      "",
  });

  const [binding, setBinding] = useState(read);

  useEffect(() => {
    const onChange = () => setBinding(read());
    window.addEventListener("storage", onChange);
    window.addEventListener("storeName:sync", onChange);
    window.addEventListener("neo:binding-changed", onChange);

    return () => {
      window.removeEventListener("storage", onChange);
      window.removeEventListener("storeName:sync", onChange);
      window.removeEventListener("neo:binding-changed", onChange);
    };
  }, []);

  return binding;
}
